import { Injectable } from "@angular/core";
import { MICustomFunctionsParams } from "../../../models/fields/custom-function-params";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { of } from "rxjs";
import { MCCodeListWrapperService, MCConfigService, MCFormisFieldAutoCompleteService, MCFormStateService } from "@maslul/common-services";
import { RequestApprovalTypeDetailsStore } from "../../store/approval-type-details-store";

@Injectable({
    providedIn: 'root'
})
export class QuantityUnitCode {

    setEveryoneDeclareUnitCode(customFunctionsObj: MICustomFunctionsParams) {
        const declaredUnitCode = this.getDeclaredUnitCodeField(customFunctionsObj);
        if (!declaredUnitCode) return;

        const isDynamic = this.isDynamicApprovalType(customFunctionsObj);

        if (isDynamic) {
            this.initDynamicBehavior(customFunctionsObj, declaredUnitCode);
        } else {
            this.initDefaultBehavior(customFunctionsObj, declaredUnitCode);
        }
    }

    updateQuantityFieldLabel(customFunctionsObj: MICustomFunctionsParams, unitCodeValue: any) {
        const quantityField = this.getQuantityField(customFunctionsObj);
        if (!quantityField) return;

        const newLabel = this.buildQuantityLabel(customFunctionsObj, unitCodeValue);

        if (quantityField.expressionProperties) {
            quantityField.expressionProperties['props.label'] = of(newLabel);
        }
    }

    // ========== Private Methods ==========

    private isDynamicApprovalType(customFunctionsObj: MICustomFunctionsParams): boolean {
        const configService = customFunctionsObj.injector.get(MCConfigService);
        const approvalTypeStore = customFunctionsObj.injector.get(RequestApprovalTypeDetailsStore);

        const approvalTypeId = parseInt(approvalTypeStore.getSnapshot().approvalTypeId);
        const dynamicTypes: number[] = configService.getConfiguration().dynamicQuantityApprovalTypes || [];

        return dynamicTypes.includes(approvalTypeId);
    }

    private getDeclaredUnitCodeField(customFunctionsObj: MICustomFunctionsParams): FormlyFieldConfig | undefined {
        const quantities = this.getQuantitiesSection(customFunctionsObj);
        if (!quantities) return undefined;

        return quantities.fieldGroup?.find(
            (x: FormlyFieldConfig) => x.key == "declaredUnitCode"
        );
    }

    private getQuantityField(customFunctionsObj: MICustomFunctionsParams): FormlyFieldConfig | undefined {
        const quantities = this.getQuantitiesSection(customFunctionsObj);
        if (!quantities) return undefined;

        return quantities.fieldGroup?.find(
            (x: FormlyFieldConfig) => x.key == "quantityByDeclaredUnit"
        );
    }

    private getQuantitiesSection(customFunctionsObj: MICustomFunctionsParams): FormlyFieldConfig | undefined {
        return customFunctionsObj.formisForm.fields
            .find((x: FormlyFieldConfig) => x.key == 'quantities');
    }

    private initDynamicBehavior(customFunctionsObj: MICustomFunctionsParams, declaredUnitCode: FormlyFieldConfig) {
        const formStateService = customFunctionsObj.injector.get(MCFormStateService);
        formStateService.setFormState('isDynamicQuantity', true);

        declaredUnitCode.hide = false;
        this.loadMeasurementUnitOptions(customFunctionsObj, declaredUnitCode);
        this.subscribeToUnitCodeChanges(customFunctionsObj, declaredUnitCode);
    }

    private initDefaultBehavior(customFunctionsObj: MICustomFunctionsParams, declaredUnitCode: FormlyFieldConfig) {
        const formStateService = customFunctionsObj.injector.get(MCFormStateService);
        formStateService.setFormState('isDynamicQuantity', false);

        declaredUnitCode.hide = true;
        this.setDefaultUnitCodeValue(customFunctionsObj, declaredUnitCode);

        // גם בהתנהגות רגילה - עדכון label של ערך פוב למוצר (יחידת מידה = "כל אחד", value=1)
        this.updateFobFieldLabel(customFunctionsObj, { value: 1, label: 'כל אחד' });
    }

    private loadMeasurementUnitOptions(customFunctionsObj: MICustomFunctionsParams, declaredUnitCode: FormlyFieldConfig) {
        const codeListService = customFunctionsObj.injector.get(MCCodeListWrapperService);
        const formisFieldAutoCompleteService = customFunctionsObj.injector.get(MCFormisFieldAutoCompleteService);

        codeListService.getList('UR_RQLMeaserType').subscribe(items => {
            if (declaredUnitCode.props) {
                declaredUnitCode.props.options = items;
                formisFieldAutoCompleteService.externalRefreshList.next(true);
            }
        });
    }

    private subscribeToUnitCodeChanges(customFunctionsObj: MICustomFunctionsParams, declaredUnitCode: FormlyFieldConfig) {
        declaredUnitCode.formControl?.valueChanges.subscribe(val => {
            if (val) {
                this.updateQuantityFieldLabel(customFunctionsObj, val);
                this.updateFobFieldLabel(customFunctionsObj, val);
            }
        });
    }

    private setDefaultUnitCodeValue(customFunctionsObj: MICustomFunctionsParams, declaredUnitCode: FormlyFieldConfig) {
        const translateService = customFunctionsObj.injector.get(TranslateService);

        translateService.stream('selectItems').subscribe(translation => {
            const quantityUnitCodeEach = translation?.quantityUnitCodeEach;

            const everyoneValue = {
                value: 1,
                label: quantityUnitCodeEach,
                additionalCode: null,
                additionalColumns: { "MaslulCode": "6" }
            };

            declaredUnitCode.formControl?.setValue(everyoneValue);
        });
    }

    private buildQuantityLabel(customFunctionsObj: MICustomFunctionsParams, unitCodeValue: any): string {
        const translateService = customFunctionsObj.injector.get(TranslateService);
        const value = unitCodeValue?.value ?? unitCodeValue;

        if (value === 1) {
            return translateService.instant('labels.quantityByDeclaredUnit');
        }

        const unitLabel = unitCodeValue?.label || '';
        const prefix = translateService.instant('labels.quantityIn');
        return prefix + unitLabel;
    }

    private updateFobFieldLabel(customFunctionsObj: MICustomFunctionsParams, unitCodeValue: any) {
        const fobField = this.getFobField(customFunctionsObj);
        if (!fobField) return;

        const newLabel = this.buildFobLabel(customFunctionsObj, unitCodeValue);
        if (fobField.expressionProperties) {
            fobField.expressionProperties['props.label'] = of(newLabel);
        }
    }

    private getFobField(customFunctionsObj: MICustomFunctionsParams): FormlyFieldConfig | undefined {
        const costDetails = customFunctionsObj.formisForm.fields
            .find((x: FormlyFieldConfig) => x.key == 'costDetails');
        if (costDetails) {
            return costDetails.fieldGroup?.find(
                (x: FormlyFieldConfig) => x.key == "apForeignCurrencyValue"
            );
        }
        // fallback - חיפוש ב-quantities
        const quantities = this.getQuantitiesSection(customFunctionsObj);
        return quantities?.fieldGroup?.find(
            (x: FormlyFieldConfig) => x.key == "apForeignCurrencyValue"
        );
    }

    private buildFobLabel(customFunctionsObj: MICustomFunctionsParams, unitCodeValue: any): string {
        const translateService = customFunctionsObj.injector.get(TranslateService);
        const value = unitCodeValue?.value ?? unitCodeValue;

        if (value === 1) {
            return translateService.instant('labels.fobPerProduct');
        }

        const unitLabel = unitCodeValue?.label || '';
        const prefix = translateService.instant('labels.fobPer');
        return prefix + unitLabel;
    }
}
